import {
  NO_STORE_POG_TRIGGER,
  type MerchflowStorePogTrigger,
} from "@CommonApi/merchflow/_merchflowId_/store-pog-trigger/all-finished";

/**
 * Newest first, by when the run happened. Trigger ids are usually monotonic,
 * but the untriggered generation is addressed as 0 and would otherwise always
 * sort last regardless of its age.
 */
export const sortStorePogVersionsNewestFirst = (versions: MerchflowStorePogTrigger[]) =>
  versions.toSorted((a, b) => {
    const byDate = Date.parse(b.created_at) - Date.parse(a.created_at);
    return byDate !== 0 ? byDate : b.store_pog_trigger_id - a.store_pog_trigger_id;
  });

/**
 * The version to show when the URL names none.
 *
 * In-progress runs are selectable but must not be the default: both FLOW_UPDATE
 * and STORE_PROP write output_store_pog rows before generation completes, so
 * defaulting to one would open the page on NOT_STARTED placeholders and point
 * Approve All at them. Falls back to the newest run of any state when nothing
 * has finished, so a merchflow mid-first-run still shows something.
 */
export const defaultStorePogVersion = (
  versions: MerchflowStorePogTrigger[] | null | undefined,
): MerchflowStorePogTrigger | undefined => {
  if (!versions || versions.length === 0) {
    return undefined;
  }
  const newestFirst = sortStorePogVersionsNewestFirst(versions);
  return newestFirst.find((version) => version.finished_at !== null) ?? newestFirst[0];
};

/**
 * A run that has not finished is labelled rather than hidden, so it is never
 * mistaken for a completed one. The untriggered generation has no trigger row
 * and so no finished_at either — that absence does not mean it is running.
 */
export const storePogVersionLabel = ({
  store_pog_trigger_id,
  finished_at,
}: MerchflowStorePogTrigger) => {
  if (store_pog_trigger_id === NO_STORE_POG_TRIGGER) {
    return "Original";
  }
  return finished_at ? String(store_pog_trigger_id) : `${store_pog_trigger_id} (in progress)`;
};
